import { useState, useEffect, useMemo } from "react";
import { User, Session } from "@supabase/supabase-js";
import { createSessionCookieManager } from "@/lib/utils/cookieManager";
import { getCookieDomain } from "@/lib/utils/domainDetection";

/**
 * Supabase Session Cookie Data interface
 */
interface SupabaseSessionData {
  access_token: string;
  token_type: string;
  expires_in: number;
  expires_at: number;
  refresh_token: string;
  user: User;
  weak_password: null;
}

/**
 * Parse URL-encoded session data from cookie
 */
function parseSessionCookie(cookieValue: string): SupabaseSessionData | null {
  try {
    // URL decode the cookie value
    const decodedValue = decodeURIComponent(cookieValue);
    return JSON.parse(decodedValue);
  } catch (error) {
    console.error("Error parsing session cookie:", error);
    return null;
  }
}

/**
 * Session data interface
 */
interface SessionData extends Partial<Session> {
  [key: string]: unknown;
}

/**
 * Check if session is expired
 */
function isSessionExpired(session: SupabaseSessionData | null): boolean {
  if (!session?.expires_at) return true;
  const now = Math.floor(Date.now() / 1000);
  return session.expires_at <= now;
}

/**
 * Custom hook to manage authentication from SUPABASE_SESSION cookie only
 */
export const useCookieAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const sessionManager = useMemo(() => {
    return createSessionCookieManager("SUPABASE_SESSION", {
      defaultDomain: getCookieDomain(),
    });
  }, []);

  useEffect(() => {
    const checkCookieAuth = () => {
      setIsLoading(true);

      try {
        // Get raw cookie value first
        const rawCookieValue = sessionManager.getCookie("SUPABASE_SESSION");

        if (!rawCookieValue) {
          setUser(null);
          setSession(null);
          setIsLoading(false);
          return;
        }

        // Parse the URL-encoded session data
        const sessionData = parseSessionCookie(rawCookieValue);

        if (!sessionData) {
          console.error("Failed to parse session cookie");
          setUser(null);
          setSession(null);
          setIsLoading(false);
          return;
        }

        // Check if session is expired
        if (isSessionExpired(sessionData)) {
          console.log("Session expired, clearing cookie");
          sessionManager.clearSession();
          setUser(null);
          setSession(null);
          setIsLoading(false);
          return;
        }

        // Use the user data directly from the session
        setUser(sessionData.user);
        setSession(sessionData as Session);
      } catch (error) {
        console.error("Error checking cookie auth:", error);
        setUser(null);
        setSession(null);
      } finally {
        setIsLoading(false);
      }
    };

    // Initial check
    checkCookieAuth();

    // Set up periodic check for session expiration
    const interval = setInterval(checkCookieAuth, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [sessionManager]);

  const signOut = () => {
    sessionManager.clearSession();
    setUser(null);
    setSession(null);
    // Notify other hooks (e.g. useCrossDomainSession) that logout occurred
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("comprehensive-logout", {
          detail: { timestamp: Date.now() },
        })
      );
    }
  };

  return {
    user,
    session,
    isLoading,
    isAuthenticated: !!user,
    signOut,
  };
};
